import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getRentalReport } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';
import { billingSummary, cycleTitle } from '../../utils/rentalBilling';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const statusColor = (status) => {
  const map = {
    Active: 'success', Overdue: 'error', Completed: 'info', Cancelled: 'default',
    pending: 'warning', partial: 'info', overdue: 'error', paid: 'success'
  };
  return map[status] || 'default';
};

const RentalReport = () => {
  const navigate = useNavigate();
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [source, setSource] = useState('all');
  const [status, setStatus] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalReport({ from, to, source, status });
      setData(res);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load rental report'));
    } finally {
      setLoading(false);
    }
  }, [from, to, source, status]);

  useEffect(() => {
    load();
    const onBranch = () => load();
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  const totals = data?.totals || {};
  const rentals = data?.rentals || [];
  const invoices = data?.invoices || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Rental report</Typography>
          <Typography variant="body2" color="text.secondary">
            Rentals, invoices, collections, and outside rent cost for a date range.
          </Typography>
        </Box>
        <ExportButtons
          title="Rental Report"
          filename="rental_report"
          columns={[
            { label: 'Rental', value: (r) => dash(r.rental_number) },
            { label: 'Customer', value: (r) => dash(r.customer?.name) },
            { label: 'Type', value: (r) => dash(r.source_label || r.rental_source) },
            { label: 'Cycle', value: (r) => dash(cycleTitle(r.billing_cycle)) },
            { label: 'Start', value: (r) => fmtExportDate(r.start_date || r.rental_date) },
            { label: 'End', value: (r) => fmtExportDate(r.end_date || r.return_date) },
            { label: 'Status', value: (r) => fmtExportStatus(r.status) },
            { label: 'Billed', value: (r) => fmtExportMoney(r.billed) },
            { label: 'Paid', value: (r) => fmtExportMoney(r.paid) },
            { label: 'Balance', value: (r) => fmtExportMoney(r.balance) }
          ]}
          rows={rentals}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField type="date" size="small" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField select size="small" label="Type" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All types</MenuItem>
          <MenuItem value="sun_systems">Sun Systems</MenuItem>
          <MenuItem value="outside">Outside</MenuItem>
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="overdue">Overdue</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Rentals', value: totals.rentals || 0 },
              { label: 'Invoices', value: totals.invoices || 0 },
              { label: 'Billed', value: money(totals.billed) },
              { label: 'Collected', value: money(totals.collected) },
              { label: 'Outstanding', value: money(totals.outstanding) },
              { label: 'Overdue', value: money(totals.overdue) },
              { label: 'Outside paid', value: money(totals.outside_paid) },
              { label: 'Profit vs outside', value: money(totals.profit) }
            ].map((card) => (
              <Grid item xs={6} md={3} key={card.label}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                    <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography fontWeight={700} gutterBottom>Rentals</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rental</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Billing</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Billed</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rentals.length === 0 ? (
                    <TableRow><TableCell colSpan={8}><Typography color="text.secondary">No rentals in this range.</Typography></TableCell></TableRow>
                  ) : rentals.map((r) => (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/${r.id}`)}>
                      <TableCell>{r.rental_number}</TableCell>
                      <TableCell>{r.customer?.name || '—'}</TableCell>
                      <TableCell>{r.source_label || r.rental_source}</TableCell>
                      <TableCell>{billingSummary(r.billing_cycle, r.duration_count)}</TableCell>
                      <TableCell>{r.start_date || r.rental_date} → {r.end_date || r.return_date}</TableCell>
                      <TableCell align="right">{money(r.billed)}</TableCell>
                      <TableCell align="right">{money(r.balance)}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={700} gutterBottom>Invoices in range</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Rental</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No invoices in this range.</Typography></TableCell></TableRow>
                  ) : invoices.map((i) => (
                    <TableRow key={i.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/invoices/${i.id}`)}>
                      <TableCell>{i.invoice_number}</TableCell>
                      <TableCell>{i.rental_number}</TableCell>
                      <TableCell>{i.due_date}</TableCell>
                      <TableCell align="right">{money(i.amount)}</TableCell>
                      <TableCell align="right">{money(i.paid_amount)}</TableCell>
                      <TableCell><Chip size="small" label={i.display_status} color={statusColor(i.display_status)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RentalReport;
